/* Created by Interactive 63 - https://interactive63.com */
/**
 * Splits compound path data into the separately drawable shapes it contains. Subpaths that sit
 * inside another (the hole of an "o", the counter of an "e") stay with the shape around them,
 * because drawn alone they would fill the hole in; every other subpath becomes its own shape.
 */
import svgpath from 'svgpath';
import { toCubicSubpaths, type Subpath } from '../morph/normalise';

/** Points taken along each curve segment when testing which subpaths sit inside which. */
const SAMPLES_PER_SEGMENT = 8;
/** Slack, in user units, when comparing bounding boxes of shapes that touch. */
const BOX_TOLERANCE = 1e-6;

export interface SplitOptions {
  /**
   * Whether open subpaths are filled, and so can be holes or shapes with holes. False for
   * stroke-only artwork, where each open subpath is an independent line.
   */
  openAsClosed: boolean;
}

interface Part {
  d: string;
  polygon: [number, number][];
  box: { x0: number; y0: number; x1: number; y1: number };
  area: number;
  /** Whether this subpath can take part in containment at all. */
  eligible: boolean;
}

/** The cubic Bézier point at t. */
function cubicPoint(
  x0: number,
  y0: number,
  c: Subpath['segs'][number],
  t: number,
): [number, number] {
  const u = 1 - t;
  const [c1x, c1y, c2x, c2y, x, y] = c;
  return [
    u * u * u * x0 + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x,
    u * u * u * y0 + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * y,
  ];
}

/** A polygon that follows a subpath closely enough to test points against. */
function polygonOf(path: Subpath): [number, number][] {
  const points: [number, number][] = [path.start];
  let [x, y] = path.start;
  for (const seg of path.segs) {
    for (let i = 1; i <= SAMPLES_PER_SEGMENT; i += 1) {
      points.push(cubicPoint(x, y, seg, i / SAMPLES_PER_SEGMENT));
    }
    x = seg[4];
    y = seg[5];
  }
  return points;
}

/** Whether a point is inside a polygon, by counting crossings of a ray to the right. */
function inside(point: [number, number], polygon: readonly [number, number][]): boolean {
  const [px, py] = point;
  let result = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i] as [number, number];
    const [xj, yj] = polygon[j] as [number, number];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) result = !result;
  }
  return result;
}

/** Area of a polygon's bounding box, used to rank which of two nested shapes is larger. */
function boxArea(box: Part['box']): number {
  return (box.x1 - box.x0) * (box.y1 - box.y0);
}

/** Whether `inner` lies wholly inside `outer`. */
function contains(outer: Part, inner: Part): boolean {
  if (!outer.eligible || !inner.eligible || outer.area <= inner.area) return false;
  const a = outer.box;
  const b = inner.box;
  const boxed =
    b.x0 >= a.x0 - BOX_TOLERANCE &&
    b.y0 >= a.y0 - BOX_TOLERANCE &&
    b.x1 <= a.x1 + BOX_TOLERANCE &&
    b.y1 <= a.y1 + BOX_TOLERANCE;
  const sample = inner.polygon[Math.floor(inner.polygon.length / 2)];
  return boxed && sample !== undefined && inside(sample, outer.polygon);
}

/** Measures one subpath given as path data. Returns null when it cannot be read. */
function measure(d: string, options: SplitOptions): Part | null {
  const parsed = toCubicSubpaths(d);
  const path = parsed.ok ? parsed.subpaths[0] : undefined;
  if (!path || path.segs.length === 0) return null;
  const polygon = polygonOf(path);
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const box = {
    x0: Math.min(...xs),
    y0: Math.min(...ys),
    x1: Math.max(...xs),
    y1: Math.max(...ys),
  };
  return {
    d,
    polygon,
    box,
    area: boxArea(box),
    eligible: path.closed || options.openAsClosed,
  };
}

/**
 * Splits path data into one path data string per separate shape, each shape keeping the subpaths
 * inside it. Returns the original data as a single entry when there is nothing to split or the
 * data cannot be read. Subpaths are written in absolute form so each stands on its own.
 */
export function splitPathData(d: string, options: SplitOptions): string[] {
  const pieces = svgpath(d)
    .abs()
    .toString()
    .split(/(?=M)/)
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '');
  if (pieces.length < 2) return [d];
  const parts = pieces.map((piece) => measure(piece, options));
  if (parts.some((part) => part === null)) return [d];
  const measured = parts as Part[];

  // Each subpath's innermost container, and how many shapes it is nested inside.
  const parent = measured.map((part, i) => {
    let best = -1;
    measured.forEach((other, j) => {
      if (j === i || !contains(other, part)) return;
      if (best === -1 || other.area < (measured[best] as Part).area) best = j;
    });
    return best;
  });
  const depthOf = (i: number): number => {
    let depth = 0;
    for (let p = parent[i] ?? -1; p !== -1; p = parent[p] ?? -1) depth += 1;
    return depth;
  };

  // Even depths are shapes; odd depths are holes in the shape around them.
  const groups = new Map<number, string[]>();
  measured.forEach((part, i) => {
    const owner = depthOf(i) % 2 === 0 ? i : (parent[i] ?? i);
    groups.set(owner, [...(groups.get(owner) ?? []), part.d]);
  });
  return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, group]) => group.join(' '));
}
